import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
  useDeferredValue,
} from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";

import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { CSS } from "@dnd-kit/utilities";

import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import toast from "react-hot-toast";
import ContextMenu from "../context/ContextMenu";
import { getRowStyle } from "../utils/utils";
import {
  createTransfer,
  updateWarehouse,
  updateWorkOrderStatus,
  batchUpdateOnlineQty,
  mirrorKpiPlannedQty,
  lockKpiQty,
  unlockKpiQty,
  splitWorkOrder,
} from "../api/workOrder";
import {
  CheckCircleIcon,
  PlayCircleIcon,
  CalendarDaysIcon,
  ClockIcon,
  BeakerIcon,
  ShieldExclamationIcon,
  QuestionMarkCircleIcon,
  EllipsisVerticalIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import NoteRow from "./NoteRow";
import { addRoomNote, getRoomNotes } from "../api/NotesApis";
import {
  batchUpdatePacking,
  createDistributor,
  getDistributorList,
} from "../api/DistrAtoD";
import { useAuth } from "../context/AuthContext";
import { SortableEventRow, SortableRow } from "./SortableRow";
import MarkCompletedConfirmModal from "../modals/MarkCompletedConfirmModal";
import RenameCustomTaskModal from "../modals/RenameCustomTaskModal";

const EditableCell = ({
  value,
  onSave,
  placeholder = "-",
  disabled = false,
}) => {
  const [editing, setEditing] = useState(false);
  const [temp, setTemp] = useState(value || "");

  useEffect(() => setTemp(value || ""), [value]);

  const handleSave = () => {
    const trimmed = temp.trim();
    if (trimmed !== value && !disabled) onSave(trimmed);
    setEditing(false);
  };

  if (!editing) {
    return (
      <div
        onClick={() => !disabled && setEditing(true)}
        className={`px-3 py-2.5 rounded-lg border border-transparent min-h-[2.8rem] flex items-center text-xs sm:text-sm font-medium text-blue-800 transition-all ${
          disabled
            ? "cursor-not-allowed opacity-60"
            : "cursor-pointer hover:border-blue-400 hover:bg-blue-50/70"
        }`}
        title={
          disabled
            ? "You do not have permission to edit distributor"
            : undefined
        }
      >
        {value || <span className="text-blue-400">{placeholder}</span>}
      </div>
    );
  }

  return (
    <input
      autoFocus
      type="text"
      value={temp}
      onChange={(e) => setTemp(e.target.value)}
      onBlur={handleSave}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === "Enter") handleSave();
      }}
      onKeyUp={(e) => {
        e.stopPropagation();
        if (e.key === "Escape") setEditing(false);
      }}
      disabled={disabled}
      className={`w-full px-3 py-2 rounded-lg border-2 text-sm font-medium text-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-200 ${
        disabled
          ? "bg-gray-100 cursor-not-allowed opacity-60"
          : "bg-white border-blue-500"
      }`}
      placeholder={placeholder}
    />
  );
};

const EditableHeader = ({ value, onSave, disabled = false }) => {
  const [editing, setEditing] = useState(false);
  const [temp, setTemp] = useState(value);

  useEffect(() => setTemp(value), [value]);

  const handleSave = () => {
    const trimmed = temp.trim();
    if (trimmed && trimmed !== value && !disabled) {
      onSave(trimmed);
      toast.success("Column header renamed!", { position: "top-right" });
    }
    setEditing(false);
  };

  if (!editing) {
    return (
      <div
        onClick={() => !disabled && setEditing(true)}
        className={`text-gray-900 px-2 py-1 rounded font-bold uppercase text-xs tracking-wider flex items-center gap-1 group ${
          disabled
            ? "cursor-not-allowed opacity-60"
            : "cursor-pointer hover:bg-white/10"
        }`}
        title={
          disabled ? "You do not have permission to rename columns" : undefined
        }
      >
        {value || "Click to name"}
        {!disabled && (
          <PencilIcon className="h-3 w-3 opacity-0 group-hover:opacity-100 transition" />
        )}
      </div>
    );
  }

  return (
    <input
      autoFocus
      type="text"
      value={temp}
      onChange={(e) => setTemp(e.target.value)}
      onBlur={handleSave}
      onKeyDown={(e) => {
        if (e.key === "Enter") handleSave();
        if (e.key === "Escape") setEditing(false);
      }}
      disabled={disabled}
      className={`w-28 px-2 py-1 text-xs font-bold text-gray-900 bg-white rounded border ${
        disabled ? "border-gray-300 opacity-60" : "border-blue-500"
      } focus:outline-none`}
      placeholder="Name..."
    />
  );
};

const BreakBadge = ({ breakTimeAdded, breaksIncluded = [], breakSummary }) => {
  if (!breakTimeAdded || breakTimeAdded <= 0) return null;

  const firstBreakLabel = breaksIncluded?.[0]?.label?.toLowerCase() || "";

  const isMorning = firstBreakLabel.includes("morning");
  const isLunch = firstBreakLabel.includes("lunch");
  const isAfternoon = firstBreakLabel.includes("afternoon");

  // Clean UI-friendly style map
  const styles = {
    morning: {
      bg: "bg-yellow-100",
      text: "text-yellow-800",
      border: "border-yellow-300",
      name: "Morning Break",
      icon: "☀️",
    },
    lunch: {
      bg: "bg-orange-100",
      text: "text-orange-800",
      border: "border-orange-300",
      name: "Lunch Break",
      icon: "🍽️",
    },
    default: {
      bg: "bg-amber-100",
      text: "text-amber-800",
      border: "border-amber-300",
      name: "Break",
      icon: "⏱️",
    },
  };

  const selectedStyle = isLunch
    ? styles.lunch
    : isMorning
      ? styles.morning
      : styles.default;

  const breakType = selectedStyle.name.toLowerCase();
  const tooltip = breakSummary || `Includes ${breakTimeAdded}m ${breakType}`;

  return (
    <span
      title={tooltip}
      className={`
        inline-flex items-center gap-1 ml-2
        px-2.5 py-0.5 rounded-full text-[10px] sm:text-xs font-semibold
        border whitespace-nowrap
        ${selectedStyle.bg} ${selectedStyle.text} ${selectedStyle.border}
      `}
    >
      <span className="text-[12px]">{selectedStyle.icon}</span>
      {selectedStyle.name}: {breakTimeAdded}m
    </span>
  );
};

const WorkOrderTable = ({
  workOrders,
  onDrop,
  onDragOver,
  onDragStart,
  isDraggingOver,
  isReadOnly,
  onContextMenuAction,
  room,
  date,
  onArchive,
  onRefreshRoom,
  canAssign = true,
  canTransferCompleted = true,
  canPlaceOnHold = true,
  canUseContextMenu = true,
  onReorder,
  onRemoveCleaningTask,
  onRenameCleaningTask,
}) => {
  const normalizeStatus = (value) => {
    if (value === null || value === undefined) return "";
    return String(value).trim().toLowerCase();
  };

  const { canWrite } = useAuth();
  const canEditDistributors = canWrite("ERPagWorkOrder");
  const canUnassign = canWrite("ERPagWorkOrder");
  const [contextMenu, setContextMenu] = useState(null);
  const [filterInput, setFilterInput] = useState({
    woNumber: "",
    projectNumber: "",
    status: "",
  });
  const filters = useDeferredValue(filterInput);
  const isFilterPending = filterInput !== filters;

  const [selectedWorkOrders, setSelectedWorkOrders] = useState(new Set());
  const [isNoSelectionModalOpen, setIsNoSelectionModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [topNote, setTopNote] = useState("");
  const [bottomNote, setBottomNote] = useState("");
  const lastSavedState = useRef(null);
  const [isSaving, setIsSaving] = useState(false);
  const [onlineValues, setOnlineValues] = useState({});
  const [packingValues, setPackingValues] = useState({});
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [showMarkConfirmModal, setShowMarkConfirmModal] = useState(false);
  const [woToMark, setWoToMark] = useState(null);

  // Split Work Order State
  const [isSplitModalOpen, setIsSplitModalOpen] = useState(false);
  const [splitQty, setSplitQty] = useState("");
  const [selectedWoForSplit, setSelectedWoForSplit] = useState(null);
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [splitDate, setSplitDate] = useState("");
  const [renameModalState, setRenameModalState] = useState({
    isOpen: false,
    taskId: null,
    title: "",
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Define handleDragEnd
  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = sortedWorkOrders.findIndex(
      (item) => (item._id || `event-${item.slot || item.type}`) === active.id,
    );
    const newIndex = sortedWorkOrders.findIndex(
      (item) => (item._id || `event-${item.slot || item.type}`) === over.id,
    );

    const newSorted = arrayMove(sortedWorkOrders, oldIndex, newIndex);

    const orderedItems = newSorted
      .filter(
        (item) =>
          (item.type === "workorder" || item.type === "manual cleaning") &&
          item._id,
      )
      .map((item) => ({
        id: item._id,
        type: item.type === "manual cleaning" ? "cleaning" : "workorder",
      }));

    try {
      await onReorder?.(room, date, orderedItems);
      toast.success("Reordered successfully!", {
        duration: 4000,
        position: "top-right",
      });
      await onRefreshRoom?.();
    } catch (err) {
      toast.error("Reorder failed – please try again");
    }
  };

  const autoSaveDistributors = useCallback(
    async (currentHeaders, currentValues) => {
      if (isReadOnly || isSaving) return;

      const headers = {};
      currentHeaders.forEach((col) => {
        if (col.label && col.label.trim()) {
          headers[col.key] = col.label.trim();
        }
      });

      const workOrdersMap = Object.entries(currentValues).reduce(
        (acc, [key, value]) => {
          const [woId, field] = key.split("_");
          if (!value.trim()) return acc;
          if (!acc[woId]) acc[woId] = { workOrderId: woId, distributors: {} };
          acc[woId].distributors[field] = value.trim();
          return acc;
        },
        {},
      );

      const workOrderList = Object.values(workOrdersMap);
      if (workOrderList.length === 0 && Object.keys(headers).length === 0)
        return;

      const payload = { room, date: date, headers, workOrders: workOrderList };
      const stateKey = JSON.stringify(payload);
      if (lastSavedState.current === stateKey) return;

      lastSavedState.current = stateKey;
      setIsSaving(true);

      try {
        await createDistributor(room, date, headers, workOrderList);
        toast.success("Distributors saved", {
          duration: 1200,
          position: "top-right",
        });

        // Re-fetch latest data
        const response = await getDistributorList(date, date, room);
        const roomData = response.data[0];

        if (roomData) {
          const { headers, workOrders } = roomData;

          setExtraColumns((prev) =>
            prev.map((col) => {
              const saved = headers.find((h) => h.key === col.key);
              return saved ? { ...col, label: saved.label } : col;
            }),
          );

          const values = {};
          workOrders.forEach((wo) => {
            const itemId = getDistributorItemId(wo);
            Object.entries(wo.distributors || {}).forEach(([key, value]) => {
              if (value && itemId) values[`${itemId}_${key}`] = value;
            });
          });
          setExtraColumnValues(values);
        }
      } catch (err) {
        console.error("Save failed:", err);
        toast.error("Failed to save distributors", { duration: 3000 });
        lastSavedState.current = null;
      } finally {
        setIsSaving(false);
      }
    },
    [room, date, isReadOnly, isSaving],
  );

  const [extraColumns, setExtraColumns] = useState([
    { key: "distA", label: "Dist A", width: 16 },
    { key: "distB", label: "Dist B", width: 16 },
    { key: "distC", label: "Dist C", width: 16 },
    { key: "distD", label: "Dist D", width: 16 },
  ]);

  const [extraColumnValues, setExtraColumnValues] = useState({});

  const renameExtraColumn = useCallback(
    (key, newLabel) => {
      setExtraColumns((prev) =>
        prev.map((col) =>
          col.key === key ? { ...col, label: newLabel } : col,
        ),
      );
      setTimeout(() => {
        autoSaveDistributors(
          extraColumns.map((col) =>
            col.key === key ? { ...col, label: newLabel } : col,
          ),
          extraColumnValues,
        );
      }, 0);
    },
    [extraColumns, extraColumnValues, autoSaveDistributors],
  );

  const updateCellValue = useCallback(
    (woId, field, value) => {
      setExtraColumnValues((prev) => {
        const newValues = { ...prev, [`${woId}_${field}`]: value };
        setTimeout(() => autoSaveDistributors(extraColumns, newValues), 400);
        return newValues;
      });
    },
    [extraColumns, autoSaveDistributors],
  );

  const getCellValue = (woId, field) => {
    return extraColumnValues[`${woId}_${field}`] || "";
  };

  const getDistributorItemId = (item) =>
    String(item?.workOrderId || item?.scheduledTaskId || item?.itemId || "");

  const isTimelineEvent = (wo) => {
    const normalizedType = String(wo?.type || "")
      .trim()
      .toLowerCase();
    return (
      wo.isTimelineEvent ||
      normalizedType === "pre cleaning" ||
      normalizedType === "changeover" ||
      normalizedType === "post cleaning" ||
      normalizedType === "manual cleaning" ||
      normalizedType === "lunch break" ||
      normalizedType === "morning break" ||
      normalizedType === "afternoon break" ||
      normalizedType === "meeting"
    );
  };

  const parseLocalDate = (dateString) => {
    const [year, month, day] = dateString.split("-").map(Number);
    return new Date(year, month - 1, day);
  };

  const clearFilters = () => {
    setFilterInput({ woNumber: "", projectNumber: "", status: "" });
  };

  const stableWorkOrders = useMemo(() => workOrders, [workOrders]);

  const filteredWorkOrders = useMemo(() => {
    return (stableWorkOrders || []).filter((wo) => {
      if (isTimelineEvent(wo)) return true;
      const matchesWoNumber = wo.woNumber
        ?.toLowerCase()
        .includes(filters.woNumber.toLowerCase());
      const matchesProjectNumber = wo.projectNumber
        ?.toLowerCase()
        .includes(filters.projectNumber.toLowerCase());
      const matchesStatus =
        !filters.status ||
        normalizeStatus(wo.status) === normalizeStatus(filters.status);

      return matchesWoNumber && matchesProjectNumber && matchesStatus;
    });
  }, [stableWorkOrders, filters]);

  const sortedWorkOrders = useMemo(() => {
    return [...filteredWorkOrders].sort((a, b) => {
      const timeA = a.startTime || a.scheduledTime || "00:00";
      const timeB = b.startTime || b.scheduledTime || "00:00";
      return timeA.localeCompare(timeB);
    });
  }, [filteredWorkOrders]);

  const completedWorkOrders = useMemo(() => {
    return sortedWorkOrders.filter(
      (wo) =>
        (wo.status === "completed" || wo.markedAsCompletedLocally === true) &&
        wo.actualQty > 0 &&
        wo.sku &&
        !["LAB HOLD", "QC HOLD"].includes(wo.warehouse || ""),
    );
  }, [sortedWorkOrders]);

  const handleSelectWorkOrder = useCallback(
    (woNumber) => {
      setSelectedWorkOrders((prev) => {
        const newSet = new Set(prev);
        const wo = stableWorkOrders.find((w) => w.woNumber === woNumber);
        if (
          wo &&
          (wo.status === "completed" || wo.markedAsCompletedLocally === true) &&
          wo.actualQty > 0 &&
          wo.sku &&
          !["LAB HOLD", "QC HOLD"].includes(wo.warehouse || "")
        ) {
          if (newSet.has(woNumber)) {
            newSet.delete(woNumber);
          } else {
            newSet.add(woNumber);
          }
        }
        return newSet;
      });
    },
    [stableWorkOrders],
  );

  const handleSelectAll = useCallback(() => {
    if (selectedWorkOrders.size === completedWorkOrders.length) {
      setSelectedWorkOrders(new Set());
    } else {
      setSelectedWorkOrders(
        new Set(completedWorkOrders.map((wo) => wo.woNumber)),
      );
    }
  }, [selectedWorkOrders, completedWorkOrders]);

  const handleTransferToStore = useCallback(async () => {
    if (selectedWorkOrders.size === 0) {
      setErrorMessage("Please select at least one completed work order.");
      setIsNoSelectionModalOpen(true);
      return;
    }
    try {
      const validWorkOrders = Array.from(selectedWorkOrders).filter(
        (woNumber) => {
          const wo = stableWorkOrders.find((w) => w.woNumber === woNumber);
          return (
            wo &&
            (wo.status === "completed" ||
              wo.markedAsCompletedLocally === true) &&
            wo.actualQty > 0 &&
            wo.sku &&
            !["LAB_HOLD", "QC_HOLD"].includes(wo.warehouse)
          );
        },
      );
      if (validWorkOrders.length === 0) {
        setErrorMessage(
          "Selected work orders are not valid for transfer. Ensure they have status 'completed', a valid SKU, and actual quantity greater than 0.",
        );
        setIsNoSelectionModalOpen(true);
        return;
      }
      const orderIds = validWorkOrders
        .map(
          (woNumber) =>
            stableWorkOrders.find((w) => w.woNumber === woNumber)._id,
        )
        .filter((id) => id);

      const onlinePayload = orderIds
        .map((woId) => ({
          workOrderId: woId,
          onlineQty: parseFloat(onlineValues[woId]) || 0,
        }))
        .filter((item) => item.onlineQty > 0);
      if (onlinePayload.length > 0) {
        await batchUpdateOnlineQty(onlinePayload);
        toast.success("Online quantities saved", {
          duration: 1200,
          position: "top-right",
        });
      }

      await createTransfer({ orderId: orderIds });
      setSelectedWorkOrders(new Set());
      setErrorMessage("");
      window.location.href = "/transfers";
      toast.success("Transfer created successfully!", {
        duration: 4000,
        position: "top-right",
      });
    } catch (err) {
      setErrorMessage(err.message || "Failed to create transfer.");
      setIsNoSelectionModalOpen(true);
    }
  }, [selectedWorkOrders, stableWorkOrders, onlineValues]);

  useEffect(() => {
    const loadNotes = async () => {
      if (!room || !date) return;
      try {
        const data = await getRoomNotes(room, date);
        setTopNote(data.topNote || "");
        setBottomNote(data.bottomNote || "");
      } catch (err) {
        console.warn("Failed to load notes:", err);
        setTopNote("");
        setBottomNote("");
      }
    };
    loadNotes();
  }, [room, date]);

  const getStatusIcon = (status) => {
    const normalizedStatus = normalizeStatus(status).replace(/\s+/g, "_");
    switch (normalizedStatus) {
      case "approved":
        return (
          <ClockIcon
            className="h-5 w-5 text-emerald-600"
            aria-label="Approved status"
          />
        );
      case "completed":
        return (
          <CheckCircleIcon
            className="h-5 w-5 text-green-600"
            aria-label="Completed status"
          />
        );
      case "in_progress":
        return (
          <PlayCircleIcon
            className="h-5 w-5 text-blue-600"
            aria-label="In Progress status"
          />
        );
      case "planned":
        return (
          <CalendarDaysIcon
            className="h-5 w-5 text-gray-600"
            aria-label="Planned status"
          />
        );
      case "LAB_HOLD":
        return (
          <BeakerIcon
            className="h-5 w-5 text-yellow-600"
            aria-label="Lab Hold status"
          />
        );
      case "QC_HOLD":
        return (
          <ShieldExclamationIcon
            className="h-5 w-5 text-orange-600"
            aria-label="QC Hold status"
          />
        );
      default:
        return (
          <QuestionMarkCircleIcon
            className="h-5 w-5 text-gray-700"
            aria-label="Unknown status"
          />
        );
    }
  };

  const closeNoSelectionModal = useCallback(() => {
    setIsNoSelectionModalOpen(false);
    setErrorMessage("");
  }, []);

  const handleContextMenu = useCallback(
    (e, wo) => {
      if (isReadOnly || wo.status === "LAB_HOLD" || wo.status === "QC_HOLD")
        return;
      e.preventDefault();
      e.stopPropagation();

      const scrollX = window.scrollX || window.pageXOffset;
      const scrollY = window.scrollY || window.pageYOffset;
      const tableContainer = e.currentTarget.closest(".overflow-x-auto");
      const containerScrollX = tableContainer ? tableContainer.scrollLeft : 0;
      const containerScrollY = tableContainer ? tableContainer.scrollTop : 0;

      setContextMenu({
        x: e.clientX + scrollX + containerScrollX,
        y: e.clientY + scrollY + containerScrollY,
        wo,
        noPermission: !canUseContextMenu,
      });
    },
    [isReadOnly, canUseContextMenu],
  );

  const handleContextMenuAction = useCallback(
    async (action, data) => {
      if (contextMenu) {
        try {
          if (action === "changeStatus") {
            await updateWorkOrderStatus(contextMenu.wo._id, data);
            toast.success(
              `Work order ${contextMenu.wo.woNumber} status updated to ${data}!`,
              {
                duration: 4000,
                position: "top-right",
              },
            );
          } else if (action === "moveToDate") {
            await onContextMenuAction(contextMenu.wo, action, data);
          } else if (action === "mirrorKpiPlannedQty") {
            await mirrorKpiPlannedQty(contextMenu.wo._id, data || "plannedQty");
            toast.success(
              `Initial quantity updated for ${contextMenu.wo.woNumber}.`,
              {
                duration: 4000,
                position: "top-right",
              },
            );
            await onRefreshRoom?.();
          } else if (action === "lockKpiQty") {
            await lockKpiQty(contextMenu.wo._id);
            toast.success(
              `Initial quantity locked for ${contextMenu.wo.woNumber}.`,
              {
                duration: 4000,
                position: "top-right",
              },
            );
            await onRefreshRoom?.();
          } else if (action === "unlockKpiQty") {
            await unlockKpiQty(contextMenu.wo._id);
            toast.success(
              `Initial quantity unlocked for ${contextMenu.wo.woNumber}.`,
              {
                duration: 4000,
                position: "top-right",
              },
            );
            await onRefreshRoom?.();
          } else if (action === "splitWorkOrder") {
            setSelectedWoForSplit(contextMenu.wo);
            setSplitQty("");
            setIsSplitModalOpen(true);
          } else {
            await onContextMenuAction(contextMenu.wo, action, data);
          }
          setContextMenu(null);
        } catch (error) {
          toast.error(
            `Failed to update work order ${contextMenu.wo.woNumber}: ${error.message}`,
            {
              duration: 4000,
              position: "top-right",
            },
          );
          console.error("Context Menu Action Error:", error);
        }
      }
    },
    [contextMenu, onContextMenuAction, onRefreshRoom],
  );

  const handleHoldAction = useCallback(
    async (wo, holdType) => {
      if (!isReadOnly) {
        try {
          const normalizedHoldType = holdType.replace("_", " ");
          await updateWarehouse(wo._id, normalizedHoldType);

          toast.success(
            `Work order ${wo.woNumber} moved to ${normalizedHoldType} successfully!`,
            {
              duration: 4000,
              position: "top-right",
            },
          );
          onRefreshRoom?.();
        } catch (error) {
          toast.error(
            `Failed to move work order ${wo.woNumber} to ${holdType.replace(
              "_",
              " ",
            )}.`,
            {
              duration: 4000,
              position: "top-right",
            },
          );
          console.error("Hold Action Error:", error);
        }
      }
    },
    [isReadOnly, onRefreshRoom],
  );

  const handleComplete = useCallback(
    (wo) => {
      if (isReadOnly || !wo.orderId) {
        toast.error("Cannot mark: missing order ID");
        return;
      }

      if (wo.isTransferred) {
        toast.error("Cannot mark already transferred work order");
        return;
      }
      if (wo.status === "completed") {
        toast.info("Work order already completed in ERPAG");
        return;
      }

      setWoToMark(wo);
      setConfirmChecked(false);
      setShowMarkConfirmModal(true);
    },
    [isReadOnly],
  );

  const openRenameCustomTaskModal = useCallback((taskId, currentTitle) => {
    if (!taskId) return;
    setRenameModalState({
      isOpen: true,
      taskId,
      title: currentTitle || "Custom Task",
    });
  }, []);

  const closeRenameCustomTaskModal = useCallback(() => {
    setRenameModalState({ isOpen: false, taskId: null, title: "" });
  }, []);

  const handleConfirmRenameCustomTask = useCallback(
    async (nextTitle) => {
      if (!renameModalState.taskId || !onRenameCleaningTask) return;
      await onRenameCleaningTask(renameModalState.taskId, nextTitle);
    },
    [renameModalState.taskId, onRenameCleaningTask],
  );

  const generateWorkOrderPDF = useCallback(() => {
    const doc = new jsPDF({ orientation: "landscape" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 10;
    let startY = 20;

    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("ASpiceAffair", margin, startY);
    doc.text(`Schedule – ${room}`, pageWidth - margin, startY, {
      align: "right",
    });
    startY += 8;

    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.text("1560 55th Ave, Montréal, QC H8T 3J5, Canada", margin, startY);
    doc.text(
      parseLocalDate(date).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      }),
      pageWidth - margin,
      startY,
      { align: "right" },
    );
    startY += 12;

    const body = [];

    const pdfBaseColumnCount = 14;
    const titleColumnIndex = 3;
    const allergenColumnIndex = 5;
    const distributionStartIndex = 8;
    const distributionEndIndex =
      distributionStartIndex + extraColumns.length - 1;
    const onlineColumnIndex = distributionEndIndex + 1;
    const totalQtyColumnIndex = distributionEndIndex + 2;
    const producedQtyColumnIndex = distributionEndIndex + 3;
    const packingColumnIndex = distributionEndIndex + 4;

    const machineToolColumnIndex = distributionEndIndex + 5;
    const qcColumnIndex = distributionEndIndex + 6;

    if (topNote) {
      body.push([
        {
          content: "NOTE: " + topNote,
          colSpan: pdfBaseColumnCount + extraColumns.length,
          styles: {
            fillColor: [255, 240, 240],
            textColor: [150, 0, 0],
            fontStyle: "bold",
            fontSize: 9,
            cellPadding: 3,
            halign: "left",
          },
        },
      ]);
    }

    filteredWorkOrders.forEach((wo) => {
      const isTimelineRow = isTimelineEvent(wo);
      const isCustomTask =
        wo.isCustomTask === true ||
        String(wo.type || "").trim().toLowerCase() === "custom";
      const machineToolValue =
        wo.machineTool || wo.machine_tool || wo.product?.machine_tool || "—";
      const packingValue =
        packingValues[wo._id] ||
        wo.packing ||
        wo.packaging ||
        wo.product?.packing ||
        wo.product?.packaging ||
        "";

      const row = [
        (wo.slot || "—") +
          (wo.totalDurationMinutes ? ` (${wo.totalDurationMinutes}m)` : ""),
        isCustomTask ? "CUSTOM TASK" : wo.woNumber,
        wo.sku || "—",
        wo.title || "—",
        wo.labelCategory || "—",
        wo.allergen || "None",
        wo.netWeight || "—",
        wo.assignedDate || "—",
      ];

      if (isCustomTask) {
        row[3] = `CUSTOM TASK - ${wo.title || "Custom Task"}`;
      }

      extraColumns.forEach((col) =>
        row.push(getCellValue(wo._id, col.key) || ""),
      );

      const onlineRaw = onlineValues[wo._id] ?? wo.onlineQty;
      const onlineNumber = Number(onlineRaw);
      const onlineValue =
        onlineRaw === null ||
        onlineRaw === undefined ||
        String(onlineRaw).trim() === "" ||
        (!Number.isNaN(onlineNumber) && onlineNumber === 0)
          ? ""
          : onlineRaw;

      row.push(
        onlineValue,
        isTimelineRow ? "" : (wo.plannedQty ?? ""),
        "",
        packingValue,
        machineToolValue,
        "",
      );

      body.push(row);
    });

    if (bottomNote) {
      body.push([
        {
          content: "NOTE: " + bottomNote,
          colSpan: pdfBaseColumnCount + extraColumns.length,
          styles: {
            fillColor: [240, 245, 255],
            textColor: [0, 0, 120],
            fontStyle: "bold",
            fontSize: 9,
            cellPadding: 3,
            halign: "left",
          },
        },
      ]);
    }

    if (filteredWorkOrders.length === 0 && !topNote && !bottomNote) {
      body.push([
        {
          content: `No schedule for ${room} on ${parseLocalDate(
            date,
          ).toLocaleDateString("en-US")}`,
          colSpan: pdfBaseColumnCount + extraColumns.length,
          styles: {
            fontStyle: "italic",
            textColor: [100, 100, 100],
            halign: "center",
          },
        },
      ]);
    }

    const baseColumns = pdfBaseColumnCount;
    const extraCount = extraColumns.length;
    const totalCols = baseColumns + extraCount;
    const availableWidth = 277;
    const colWidth = availableWidth / totalCols;
    const colWidths = Array(totalCols).fill(colWidth);

    if (totalCols > 1) {
      const woColumnIndex = 1;
      const woExtraWidth = 2.2;
      const giveBackPerCol = woExtraWidth / (totalCols - 1);
      for (let i = 0; i < totalCols; i += 1) {
        if (i === woColumnIndex) {
          colWidths[i] += woExtraWidth;
        } else {
          colWidths[i] = Math.max(4, colWidths[i] - giveBackPerCol);
        }
      }
    }

    const transferWidth = (fromIndex, toIndex, amount) => {
      if (
        fromIndex < 0 ||
        toIndex < 0 ||
        fromIndex >= colWidths.length ||
        toIndex >= colWidths.length
      ) {
        return;
      }
      const availableToMove = Math.max(0, colWidths[fromIndex] - 4);
      const moved = Math.min(amount, availableToMove);
      colWidths[fromIndex] -= moved;
      colWidths[toIndex] += moved;
    };

    transferWidth(6, 3, 1.2);
    transferWidth(producedQtyColumnIndex, 3, 1.0);
    // transferWidth(qcColumnIndex, 3, 2.5);
    transferWidth(qcColumnIndex, 3, 6.0);
    transferWidth(6, 2, 0.3);
    transferWidth(machineToolColumnIndex, 2, 1.0);
    transferWidth(qcColumnIndex, packingColumnIndex, 2.0);
  //  transferWidth(qcColumnIndex, 2, 0.3);

    autoTable(doc, {
      startY,
      head: [
        [
          "Hour",
          "WO",
          "SKU",
          "Title",
          "Label",
          "Allergen",
          "Net Wt",
          "Assigned",
          ...extraColumns.map((c) => c.label),
          "Online Canada",
          "Total Quantity",
          "Produced Quantity",
          "Packing / Notes",
          "M-Tool",
          "QC",
        ],
      ],
      body,
      theme: "grid",
      styles: {
        fontSize: 7,
        cellPadding: 1.2,
        overflow: "linebreak",
        minCellHeight: 4,
      },
      headStyles: {
        fillColor: [0, 0, 0],
        textColor: [255, 255, 255],
        fontSize: 7,
      },
      margin: { left: margin, right: margin },
      columnStyles: colWidths.reduce(
        (acc, w, i) => ({ ...acc, [i]: { cellWidth: w } }),
        {},
      ),
      didParseCell: (data) => {
      if (data.column.index === 3 && data.cell.text[0]?.length > 45) {
        data.cell.text[0] = data.cell.text[0].slice(0, 42) + "...";
      }

      if (data.section === "body") {
        const colIndex = data.column.index;
        const isCustomTaskRow =
          String(data.row.raw?.[1] || "").toUpperCase() === "CUSTOM TASK";

        if (isCustomTaskRow) {
          data.cell.styles.fillColor = [253, 232, 255];
          data.cell.styles.textColor = [112, 26, 117];
          data.cell.styles.fontStyle = "bold";
        }

        const allergenCell = String(
          data.row.raw?.[allergenColumnIndex] || "",
        ).trim();
        const hasAllergen =
          allergenCell !== "" &&
          allergenCell.toLowerCase() !== "none" &&
          allergenCell !== "—";
        if (hasAllergen && colIndex === allergenColumnIndex) {
          data.cell.styles.textColor = [180, 0, 0];
          data.cell.styles.fontStyle = "bold";
        }

          if (colIndex === allergenColumnIndex) {
            data.cell.styles.fontStyle = "bold";
            data.cell.styles.fontSize = 7.5;
          }
           if (colIndex === titleColumnIndex) {
    data.cell.styles.fontStyle = "bold";
    data.cell.styles.fontSize = 8;
  }

          if (
            colIndex === onlineColumnIndex ||
            colIndex === totalQtyColumnIndex ||
            colIndex === producedQtyColumnIndex 
          ) {
            data.cell.styles.fontStyle = "bold";
            data.cell.styles.fontSize = 8.8;
          }

          if (
            colIndex >= distributionStartIndex &&
            colIndex <= distributionEndIndex
          ) {
            data.cell.styles.fontSize = 8.8;
            data.cell.styles.fontStyle = "bold";
          }
          if (
            colIndex === packingColumnIndex ||
            colIndex === machineToolColumnIndex
          ) {
            data.cell.styles.fontSize = 8;
          }
        }
      },
    });

    doc.setFontSize(8);
    doc.setFont("helvetica", "italic");
    doc.text(
      "Please verify work order details before use.",
      margin,
      doc.internal.pageSize.getHeight() - 10,
    );

    const url = doc.output("bloburl");
    window.open(url, "_blank");
    toast.success("PDF generated – numbers are now larger & key columns bold!");
  }, [
    date,
    room,
    filteredWorkOrders,
    topNote,
    bottomNote,
    extraColumns,
    extraColumnValues,
    onlineValues,
    packingValues,
  ]);

  const totals = useMemo(() => {
    return sortedWorkOrders.reduce(
      (acc, wo) => {
        acc.planned += wo.plannedQty || 0;
        acc.actual += wo.actualQty || 0;
        return acc;
      },
      { planned: 0, actual: 0 },
    );
  }, [sortedWorkOrders]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isNoSelectionModalOpen) closeNoSelectionModal();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isNoSelectionModalOpen, closeNoSelectionModal]);

  const saveRoomNote = useCallback(
    async (type, newText) => {
      setIsSavingNote(true);
      const payload = {
        room,
        date: date,
        topNote: type === "top" ? newText : topNote,
        bottomNote: type === "bottom" ? newText : bottomNote,
      };

      try {
        await addRoomNote(payload);
        if (type === "top") setTopNote(newText);
        else setBottomNote(newText);
        toast.success("Note saved!");
      } catch (err) {
        toast.error("Save failed - try again");
        throw err;
      } finally {
        setIsSavingNote(false);
      }
    },
    [date, room, topNote, bottomNote],
  );

  useEffect(() => {
    if (!room || !date || isReadOnly) return;

    const loadData = async () => {
      try {
        const response = await getDistributorList(date, date, room);
        const roomData = response.data[0];

        if (roomData) {
          const { headers, workOrders } = roomData;

          setExtraColumns((prev) =>
            prev.map((col) => {
              const saved = headers.find((h) => h.key === col.key);
              return saved ? { ...col, label: saved.label } : col;
            }),
          );

          const values = {};
          const packingVals = {};
          workOrders.forEach((wo) => {
            const itemId = getDistributorItemId(wo);
            Object.entries(wo.distributors || {}).forEach(([key, value]) => {
              if (value && itemId) values[`${itemId}_${key}`] = value;
            });
            if (itemId && wo.packing !== undefined && wo.packing !== null) {
              packingVals[itemId] = wo.packing;
            }
          });
          setExtraColumnValues(values);
          setPackingValues(packingVals);
        } else {
          setExtraColumns([
            { key: "distA", label: "Dist A", width: 16 },
            { key: "distB", label: "Dist B", width: 16 },
            { key: "distC", label: "Dist C", width: 16 },
            { key: "distD", label: "Dist D", width: 16 },
          ]);
          setExtraColumnValues({});
          setOnlineValues({});
          setPackingValues({});
        }
      } catch (err) {
        console.warn("Failed to load data:", err);
        toast.error("Failed to load distributor/online data");
      }
    };

    loadData();
  }, [room, date, isReadOnly]);

  useEffect(() => {
    const onlineVals = {};
    workOrders.forEach((wo) => {
      if (wo.onlineQty !== undefined && wo.onlineQty !== null) {
        onlineVals[wo._id] = wo.onlineQty;
      }
    });
    setOnlineValues(onlineVals);

    setPackingValues((prev) => {
      const next = { ...prev };
      workOrders.forEach((wo) => {
        const woPacking =
          wo.packing ??
          wo.packaging ??
          wo.product?.packing ??
          wo.product?.packaging ??
          "";

        if (
          (next[wo._id] === undefined || next[wo._id] === null || next[wo._id] === "") &&
          woPacking
        ) {
          next[wo._id] = woPacking;
        }
      });
      return next;
    });
  }, [workOrders]);

  const autoSaveOnlineQty = useCallback(
    async (woId, value) => {
      const wo = workOrders.find((w) => w._id === woId);
      if (!wo) return;

      const actualQty = wo.actualQty || 0;
      const numValue = parseFloat(value);

      // Validation 1: Must be a valid number
      if (isNaN(numValue)) {
        toast.error("Only numbers are allowed", { position: "top-right" });
        setOnlineValues((prev) => ({ ...prev, [woId]: wo.onlineQty || 0 }));
        return;
      }

      // Validation 2: Cannot be negative
      if (numValue < 0) {
        toast.error("Online quantity cannot be negative", {
          position: "top-right",
        });
        setOnlineValues((prev) => ({ ...prev, [woId]: wo.onlineQty || 0 }));
        return;
      }

      // Validation 3: Cannot exceed actualQty
      if (numValue > actualQty) {
        toast.error(`Cannot exceed actual Qty (${actualQty})`, {
          position: "top-right",
        });
        setOnlineValues((prev) => ({ ...prev, [woId]: wo.onlineQty || 0 }));
        return;
      }

      try {
        await batchUpdateOnlineQty([
          { workOrderId: woId, onlineQty: numValue },
        ]);
        toast.success("Online quantity saved", { duration: 1000 });
      } catch (err) {
        toast.error("Failed to save online quantity", {
          position: "top-right",
        });
        console.error(err);
        setOnlineValues((prev) => ({ ...prev, [woId]: wo.onlineQty || 0 }));
      }
    },
    [workOrders],
  );

  const saveTimeoutRef = useRef({});

  const updateOnlineValue = useCallback(
    (woId, value) => {
      setOnlineValues((prev) => ({ ...prev, [woId]: value }));

      const timerKey = `online_${woId}`;
      if (saveTimeoutRef.current[timerKey]) {
        clearTimeout(saveTimeoutRef.current[timerKey]);
      }

      saveTimeoutRef.current[timerKey] = setTimeout(() => {
        autoSaveOnlineQty(woId, value);
      }, 800);
    },
    [autoSaveOnlineQty],
  );

  const autoSavePacking = useCallback(
    async (woId, value) => {
      const wo = workOrders.find((w) => w._id === woId);
      if (!wo) return;

      try {
        await batchUpdatePacking(room, date, [
          { workOrderId: woId, packing: value || "" },
        ]);
        toast.success("Packing saved", { duration: 1000 });
      } catch (err) {
        toast.error("Failed to save packing", { position: "top-right" });
        console.error(err);
        try {
          const response = await getDistributorList(date, date, room);
          const roomData = response.data[0];
          const packingVals = {};
          (roomData?.workOrders || []).forEach((item) => {
            const itemId = getDistributorItemId(item);
            if (itemId && item.packing !== undefined && item.packing !== null) {
              packingVals[itemId] = item.packing;
            }
          });
          setPackingValues(packingVals);
        } catch (refreshErr) {
          setPackingValues((prev) => ({ ...prev, [woId]: wo.packing || "" }));
        }
      }
    },
    [workOrders, room, date],
  );

  const updatePackingValue = useCallback(
    (woId, value) => {
      setPackingValues((prev) => ({ ...prev, [woId]: value }));

      const timerKey = `packing_${woId}`;
      if (saveTimeoutRef.current[timerKey]) {
        clearTimeout(saveTimeoutRef.current[timerKey]);
      }

      saveTimeoutRef.current[timerKey] = setTimeout(() => {
        autoSavePacking(woId, value);
      }, 800);
    },
    [autoSavePacking],
  );

  return (
    <div
      className={`bg-white p-5 sm:p-7 rounded-lg shadow-md border border-gray-200 transition-all duration-300 ${
        isDraggingOver && !isReadOnly
          ? "border-2 border-dashed border-gray-300 bg-gray-50"
          : ""
      }`}
      onDrop={isReadOnly ? undefined : onDrop}
      onDragOver={isReadOnly ? undefined : onDragOver}
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-3">
        <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
          Work Order Details - {room} (
          {parseLocalDate(date).toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
            year: "numeric",
          })}
          )
        </h3>
        <button
          onClick={generateWorkOrderPDF}
          className="flex items-center gap-2 py-2 px-4 rounded-lg bg-black hover:bg-gray-900 text-white text-sm font-medium shadow-sm transition"
          aria-label="Generate work orders PDF"
        >
          Print Schedule
        </button>
      </div>

      {/* Error */}
      {errorMessage && (
        <div className="mb-4 p-3 bg-red-100 text-red-800 text-sm rounded border border-red-300">
          {errorMessage}
        </div>
      )}

      {/* Filters */}
      <div className="mb-4 sm:mb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="flex flex-col">
          <label
            htmlFor="woNumber"
            className="text-sm font-medium text-gray-700 mb-1"
          >
            WO Number
          </label>
          <input
            id="woNumber"
            type="text"
            value={filterInput.woNumber}
            onChange={(e) =>
              setFilterInput((prev) => ({ ...prev, woNumber: e.target.value }))
            }
            placeholder="Enter WO Number"
            className="w-full py-2 px-3 rounded-lg border border-gray-300 text-sm text-gray-900 focus:ring-2 focus:ring-[#365daf] focus:border-[#365daf] transition"
          />
        </div>
        <div className="flex flex-col">
          <label
            htmlFor="projectNumber"
            className="text-sm font-medium text-gray-700 mb-1"
          >
            Project Number
          </label>
          <input
            id="projectNumber"
            type="text"
            value={filterInput.projectNumber}
            onChange={(e) =>
              setFilterInput((prev) => ({
                ...prev,
                projectNumber: e.target.value,
              }))
            }
            placeholder="Enter Project Number"
            className="w-full py-2 px-3 rounded-lg border border-gray-300 text-sm text-gray-900 focus:ring-2 focus:ring-[#365daf] focus:border-[#365daf] transition"
          />
        </div>
        <div className="flex flex-col">
          <label
            htmlFor="status"
            className="text-sm font-medium text-gray-700 mb-1"
          >
            Status
          </label>
          <select
            id="status"
            value={filterInput.status}
            onChange={(e) =>
              setFilterInput((prev) => ({ ...prev, status: e.target.value }))
            }
            className="w-full py-2 px-3 rounded-lg border border-gray-300 text-sm text-gray-900 focus:ring-2 focus:ring-[#365daf] focus:border-[#365daf] transition"
          >
            <option value="">All Statuses</option>
            <option value="approved">Approved</option>
            <option value="planned">Planned</option>
            <option value="in progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="LAB_HOLD">Lab Hold</option>
            <option value="QC_HOLD">QC Hold</option>
          </select>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4 mb-4">
        <button
          onClick={clearFilters}
          className="flex items-center gap-2 py-2 px-4 rounded-lg text-white bg-[#cf350d] hover:bg-red-700 text-sm font-medium transition"
        >
          Clear Filters
        </button>
        {!canTransferCompleted && selectedWorkOrders.size > 0 && (
          <div className="mb-3 text-xs text-amber-700 bg-amber-50 px-3 py-2 rounded border border-amber-200">
            You do not have permission to transfer completed work orders.
          </div>
        )}
        <button
          onClick={handleTransferToStore}
          disabled={
            isReadOnly ||
            selectedWorkOrders.size === 0 ||
            !canTransferCompleted ||
            Array.from(selectedWorkOrders).some((woNumber) => {
              const wo = stableWorkOrders.find((w) => w.woNumber === woNumber);
              return wo && ["LAB HOLD", "QC HOLD"].includes(wo.warehouse || "");
            })
          }
          className={`flex items-center justify-center gap-2 py-2 px-4 rounded-lg bg-black hover:bg-gray-900 text-white text-sm font-medium transition-all duration-200
    disabled:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-50
    ${!canTransferCompleted ? "opacity-70" : ""}`}
          title={
            Array.from(selectedWorkOrders).some((woNumber) => {
              const wo = stableWorkOrders.find((w) => w.woNumber === woNumber);
              return wo && ["LAB HOLD", "QC HOLD"].includes(wo.warehouse || "");
            })
              ? "Cannot transfer work orders on LAB HOLD or QC HOLD"
              : !canTransferCompleted
                ? "You do not have permission to transfer completed work orders"
                : ""
          }
        >
          Transfer to Store
        </button>
      </div>

      {/* Desktop Table */}
      <div className="hidden sm:block overflow-x-auto">
        <table className="w-full border-collapse mt-4">
          <thead>
            <tr>
              {[
                { key: "checkbox", label: "" },
                { key: "slot", label: "Scheduled Hour" },
                { key: "woNumber", label: "WO Number" },
                { key: "projectNumber", label: "Project #" },
                { key: "sku", label: "SKU" },
                { key: "title", label: "Title" },
                { key: "labelCategory", label: "Label" },
                { key: "allergen", label: "Allergen" },
                { key: "netWeight", label: "Net Weight" },
                { key: "plannedQty", label: "Planned Qty" },
                { key: "actualQty", label: "Actual Qty" },
                { key: "status", label: "Status" },
                { key: "lotNumber", label: "Lot Number" },
                { key: "assignedDate", label: "Assigned Date" },
                ...extraColumns.map((col) => ({
                  key: col.key,
                  label: col.label,
                })),
                { key: "online", label: "Online Canada" },
                { key: "packing", label: "Packing/Notes" },
                { key: "actions", label: "Actions" },
              ].map((col) => (
                <th
                  key={col.key}
                  className="py-3 px-4 bg-gray-200 text-gray-900 font-bold uppercase text-xs border-b-4 border-gray-700"
                >
                  {col.key === "checkbox" ? (
                    <input
                      type="checkbox"
                      checked={
                        selectedWorkOrders.size ===
                          completedWorkOrders.length &&
                        completedWorkOrders.length > 0
                      }
                      onChange={handleSelectAll}
                      disabled={isReadOnly || completedWorkOrders.length === 0}
                      className="w-4 h-4 rounded border-gray-300"
                    />
                  ) : extraColumns.some((c) => c.key === col.key) ? (
                    <EditableHeader
                      value={col.label}
                      onSave={(v) => renameExtraColumn(col.key, v)}
                      disabled={!canEditDistributors}
                    />
                  ) : col.key === "online" || col.key === "packing" ? (
                    <span className="tracking-wider">{col.label}</span>
                  ) : (
                    <span className="tracking-wider">{col.label}</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              modifiers={[restrictToVerticalAxis]}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={sortedWorkOrders.map(
                  (wo) => wo._id || `event-${wo.slot || wo.type}`,
                )}
                strategy={verticalListSortingStrategy}
              >
                {/* Top Note - fixed (not sortable) */}
                <tr className="border-t-4 border-l-0 border-r-0 border-b-0 border-red-600 bg-red-50/40">
                  <td className="py-2 px-4"></td>
                  <td className="py-2 px-4" colSpan={16 + extraColumns.length}>
                    <NoteRow
                      note={topNote}
                      noteType="top"
                      onSave={saveRoomNote}
                      placeholder="Add top note eg: Urgent-complete online stock as priority"
                      colorClass="text-red-800 font-semibold"
                      counterClass="text-red-600"
                    />
                  </td>
                </tr>

                {sortedWorkOrders.length > 0 ? (
                  sortedWorkOrders.map((wo) => {
                    const id = wo._id || `event-${wo.slot || wo.type}`;
                    const normalizedType = String(wo?.type || "")
                      .trim()
                      .toLowerCase();
                    const isWorkOrder =
                      (normalizedType === "workorder" ||
                        normalizedType === "work order") &&
                      !!wo._id;
                    const isEvent = isTimelineEvent(wo);

                    if (!isWorkOrder) {
                      if (wo.type === "manual cleaning") {
                        return (
                          <SortableEventRow
                            key={id}
                            id={id}
                            event={wo}
                            isReadOnly={isReadOnly}
                            onRemoveCleaningTask={onRemoveCleaningTask}
                            onRenameCleaningTask={openRenameCustomTaskModal}
                            extraColumns={extraColumns}
                            getStatusIcon={getStatusIcon}
                            packingValues={packingValues}
                            updatePackingValue={updatePackingValue}
                            canEditDistributors={canEditDistributors}
                          />
                        );
                      }

                      // Timeline events (breaks, pre/post cleaning, changeover, meeting) — not draggable
                      return (
                        <tr
                          key={id}
                          className={`p-3 rounded-lg shadow-sm border-2 transition-all duration-200 ${
                            wo.type.includes("break")
                              ? "bg-amber-50 border-amber-400 text-amber-900 font-medium shadow-md"
                              : wo.type === "meeting"
                              ? "bg-blue-50 border-blue-400 text-blue-900 font-medium shadow-md"
                              : "bg-gray-50 text-gray-700 italic border-gray-300"
                          }`}
                          style={getRowStyle(wo.allergen, wo.status, room)}
                        >
                          {/* Checkbox column - empty for events */}
                          <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-xs sm:text-sm" />

                          {/* Slot / Time */}
                          <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-xs sm:text-sm text-green-700 font-medium">
                            {wo.slot} ({wo.duration} min)
                          </td>

                          {/* Title (for events) or WO Number */}
                          <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-md sm:text-sm select-text">
                            {wo.title}
                          </td>

                          {/* Other columns for events - mostly "—" */}
                          <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-md sm:text-sm select-text">
                            —
                          </td>
                          <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-md sm:text-sm select-text">
                            —
                          </td>
                          <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-md sm:text-sm select-text">
                            —
                          </td>
                          <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-md sm:text-sm select-text">
                            —
                          </td>
                          <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-md sm:text-sm select-text">
                            —
                          </td>
                          <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-md sm:text-sm select-text">
                            —
                          </td>
                          <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-md sm:text-sm select-text">
                            —
                          </td>
                          <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-md sm:text-sm select-text">
                            —
                          </td>
                          <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-xs sm:text-sm">
                            {getStatusIcon(wo.status || "—")}
                          </td>
                          <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-md sm:text-sm select-text">
                            —
                          </td>
                          <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-md sm:text-sm select-text">
                            —
                          </td>

                          {/* Extra columns - "—" for events */}
                          {extraColumns.map((col) => (
                            <td
                              key={col.key}
                              className="py-2.5 px-3 border-b border-gray-200"
                            >
                              <div className="px-3 py-2.5 text-xs sm:text-sm text-gray-500 italic min-h-[2.8rem] flex items-center">
                                —
                              </div>
                            </td>
                          ))}

                          {/* Online - "—" for events */}
                          <td className="py-2.5 px-3 border-b border-gray-200">
                            <div className="px-3 py-2.5 text-xs sm:text-sm text-gray-500 italic min-h-[2.8rem] flex items-center">
                              —
                            </div>
                          </td>
                          <td className="py-2.5 px-3 border-b border-gray-200">
                            <div className="px-3 py-2.5 text-xs sm:text-sm text-gray-500 italic min-h-[2.8rem] flex items-center">
                              —
                            </div>
                          </td>

                          {/* Actions */}
                          <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-xs sm:text-sm">
                            {wo.type === "manual cleaning" &&
                              onRemoveCleaningTask &&
                              !isReadOnly && (
                                <button
                                  onClick={() => onRemoveCleaningTask(wo._id)}
                                  className="px-2 py-1 text-xs rounded bg-red-800 text-white hover:bg-red-600 mt-1"
                                >
                                  Remove
                                </button>
                              )}
                          </td>
                        </tr>
                      );
                    }

                    return (
                      <SortableRow
                        key={id}
                        id={id}
                        wo={wo}
                        getStatusIcon={getStatusIcon}
                        room={room}
                        isReadOnly={isReadOnly}
                        canPlaceOnHold={canPlaceOnHold}
                        canUnassign={canUnassign}
                        onContextMenuAction={onContextMenuAction}
                        handleHoldAction={handleHoldAction}
                        handleComplete={handleComplete}
                        extraColumns={extraColumns}
                        getCellValue={getCellValue}
                        updateCellValue={updateCellValue}
                        onlineValues={onlineValues}
                        updateOnlineValue={updateOnlineValue}
                        packingValues={packingValues}
                        updatePackingValue={updatePackingValue}
                        selectedWorkOrders={selectedWorkOrders}
                        handleSelectWorkOrder={handleSelectWorkOrder}
                        canEditDistributors={canEditDistributors}
                        onOpenActionMenu={handleContextMenu}
                      />
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan={17 + extraColumns.length}
                      className="text-center py-8 sm:py-12 text-gray-600 text-sm sm:text-base"
                    >
                      {isReadOnly
                        ? "No work orders or tasks for this day"
                        : "Drag a work order or task here!"}
                    </td>
                  </tr>
                )}

                {/* Bottom Note - fixed */}
                <tr className="border-t-0 border-l-0 border-r-0 border-b-4 border-blue-500 bg-blue-50/30">
                  <td className="py-2 px-4"></td>
                  <td className="py-2 px-4" colSpan={16 + extraColumns.length}>
                    <NoteRow
                      note={bottomNote}
                      noteType="bottom"
                      onSave={saveRoomNote}
                      placeholder="Add bottom Note eg Pick up at 4pm, call supplier..."
                      colorClass="text-blue-900 font-medium"
                      counterClass="text-blue-600"
                    />
                  </td>
                </tr>
              </SortableContext>
            </DndContext>
          </tbody>
          <tfoot>
            <tr>
              <td
                colSpan="9"
                className="py-2 sm:py-3 px-2 sm:px-4 font-semibold text-gray-900 text-xs sm:text-sm bg-gray-100"
              >
                <strong>Subtotal</strong>
              </td>
              <td className="py-2 sm:py-3 px-2 sm:px-4 font-semibold text-gray-900 text-xs sm:text-sm text-right bg-gray-100">
                <strong>{totals.planned.toFixed(2)}</strong>
              </td>
              <td className="py-2 sm:py-3 px-2 sm:px-4 font-semibold text-gray-900 text-xs sm:text-sm text-right bg-gray-100">
                <strong>{totals.actual.toFixed(2)}</strong>
              </td>
              <td className="py-2 sm:py-3 px-2 sm:px-4 bg-gray-100"></td>
              <td className="py-2 sm:py-3 px-2 sm:px-4 bg-gray-100"></td>
              <td className="py-2 sm:py-3 px-2 sm:px-4 bg-gray-100"></td>
              <td className="py-2 sm:py-3 px-2 sm:px-4 bg-gray-100"></td>
              {extraColumns.map((_, i) => (
                <td
                  key={i}
                  className="py-2 sm:py-3 px-2 sm:px-4 bg-gray-100"
                ></td>
              ))}
              <td className="py-2 sm:py-3 px-2 sm:px-4 bg-gray-100"></td>
              <td className="py-2 sm:py-3 px-2 sm:px-4 bg-gray-100"></td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="block sm:hidden mt-4 space-y-3">
        <div className="px-3 py-2 border-b-4 border-red-600 bg-red-50/40 rounded-t-lg">
          <NoteRow
            note={topNote}
            noteType="top"
            onSave={saveRoomNote}
            placeholder="Urgent – complete online stock as priority"
            colorClass="text-red-800 font-semibold"
            counterClass="text-red-600"
          />
        </div>

        {sortedWorkOrders.length > 0 ? (
          sortedWorkOrders.map((wo) => {
            const isEvent = isTimelineEvent(wo);
            const normalizedType = String(wo?.type || "")
              .trim()
              .toLowerCase();
            const isWorkOrder =
              normalizedType === "workorder" || normalizedType === "work order";
            const isCustomTask =
              wo.isCustomTask === true || normalizedType === "custom";
            const isCompleted = isWorkOrder && wo.status === "completed";
            const isOnHold =
              isWorkOrder &&
              !wo.isTransferred &&
              ["LAB HOLD", "QC HOLD"].includes(wo.warehouse || "");
            const isDraggable =
              canAssign && !isReadOnly && !isOnHold && !isEvent && !wo.isSplitCopy;
            const isTask = isWorkOrder && !wo.sku;
            const rowStyle = isEvent
              ? {}
              : getRowStyle(wo.allergen, wo.status, room);

            return (
              <div
                key={wo._id || (isWorkOrder ? wo.woNumber : `event-${wo.slot}`)}
                draggable={isDraggable}
                onDragStart={
                  isDraggable && isWorkOrder
                    ? (e) => onDragStart?.(e, wo)
                    : undefined
                }
                className={`transition-all duration-200 ${
                  isEvent
                    ? isCustomTask
                      ? "bg-purple-50 border-l-4 border-purple-200 text-purple-900 font-medium shadow-sm"
                      : wo.type.includes("break")
                      ? "bg-amber-50 border-l-4 border-amber-500 text-amber-900 font-medium"
                      : "bg-gray-50 text-gray-700 italic"
                    : isDraggable
                      ? "cursor-grab hover:bg-gray-100"
                      : ""
                }`}
                style={rowStyle}
                tabIndex={isDraggable ? 0 : -1}
              >
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="font-semibold text-gray-800 col-span-2 flex items-center gap-1">
                    {isCustomTask ? (
                      <>
                        <span className="inline-flex items-center rounded-full border border-purple-200 bg-purple-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-purple-800">
                          Custom Task
                        </span>
                        <span className="text-purple-900">
                          {wo.title || "Custom Task"}
                        </span>
                      </>
                    ) : isEvent ? (
                      wo.title
                    ) : (
                      wo.woNumber
                    )}
                    {!isEvent && wo.isSplitCopy && (
                      <span title="Split Copy" className="text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-800 border border-indigo-300 whitespace-nowrap">
                        ↳ Split
                      </span>
                    )}
                  </div>
                  <div className="text-green-700 font-medium col-span-2">
                    {isEvent ? (
                      <>
                        <span>
                          {wo.slot} ({wo.duration} min)
                        </span>
                        {wo.breakTimeAdded > 0 && (
                          <BreakBadge
                            breakTimeAdded={wo.breakTimeAdded}
                            breaksIncluded={wo.breaksIncluded}
                            breakSummary={wo.breakSummary}
                          />
                        )}
                      </>
                    ) : (
                      <>
                        {wo.slot || "Not Scheduled"}
                        {wo.totalDurationMinutes
                          ? ` (${wo.totalDurationMinutes} min)`
                          : ""}
                        {wo.type === "workorder" && wo.breakTimeAdded > 0 && (
                          <BreakBadge
                            breakTimeAdded={wo.breakTimeAdded}
                            breaksIncluded={wo.breaksIncluded}
                            breakSummary={wo.breakSummary}
                          />
                        )}
                      </>
                    )}
                  </div>

                  {isWorkOrder && (
                    <>
                      <div className="font-medium text-gray-700">Project #</div>
                      <div className="select-text">{wo.projectNumber}</div>

                      <div className="font-medium text-gray-700">SKU</div>
                      <div className="select-text">{wo.sku || "N/A"}</div>

                      <div className="font-medium text-gray-700">Title</div>
                      <div className="select-text truncate max-w-[140px]">
                        {wo.title}
                      </div>

                      <div className="font-medium text-gray-700">Status</div>
                      <div>{getStatusIcon(wo.status)}</div>

                      <div className="font-medium text-gray-700">Qty</div>
                      <div>
                        <span className="text-gray-600">Plan:</span>{" "}
                        {wo.plannedQty || 0} |
                        <span className="text-gray-600 ml-1">Act:</span>{" "}
                        {wo.actualQty || 0}
                      </div>

                      {/* Extra Columns */}
                      {extraColumns.map((col) => (
                        <React.Fragment key={col.key}>
                          <div className="font-medium text-gray-700">
                            {col.label}
                          </div>
                          <div>
                            {isTimelineEvent(wo) ? (
                              <span className="text-gray-500 italic text-xs">
                                —
                              </span>
                            ) : (
                              <EditableCell
                                value={getCellValue(wo._id, col.key)}
                                onSave={(v) =>
                                  updateCellValue(wo._id, col.key, v)
                                }
                              />
                            )}
                          </div>
                        </React.Fragment>
                      ))}
                      <React.Fragment>
                        <div className="font-medium text-gray-700">Online</div>
                        <div>
                          {isTimelineEvent(wo) ? (
                            <span className="text-gray-500 italic text-xs">
                              —
                            </span>
                          ) : (
                            <EditableCell
                              value={onlineValues[wo._id] || ""}
                              onSave={(v) => updateOnlineValue(wo._id, v)}
                              disabled={!canEditDistributors || isReadOnly}
                            />
                          )}
                        </div>
                      </React.Fragment>
                      <React.Fragment>
                        <div className="font-medium text-gray-700">Packing</div>
                        <div>
                          {isTimelineEvent(wo) ? (
                            <span className="text-gray-500 italic text-xs">
                              —
                            </span>
                          ) : (
                            <EditableCell
                              value={packingValues[wo._id] || ""}
                              onSave={(v) => updatePackingValue(wo._id, v)}
                              disabled={!canEditDistributors || isReadOnly}
                            />
                          )}
                        </div>
                      </React.Fragment>

                      {/* Actions */}
                      <div className="col-span-2 mt-2">
                        <button
                          type="button"
                          onClick={(event) => handleContextMenu(event, wo)}
                          className="mb-2 inline-flex h-8 w-8 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-700 shadow-sm hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-500"
                          title="Work order actions"
                          aria-label={`Open actions for work order ${wo.woNumber}`}
                        >
                          <EllipsisVerticalIcon className="h-5 w-5" />
                        </button>

                        {isCompleted &&
                          !isOnHold &&
                          !isReadOnly &&
                          !wo.isTransferred && (
                            <div className="flex gap-2 flex-wrap">
                              <button
                                onClick={() => handleHoldAction(wo, "LAB_HOLD")}
                                className="px-2 py-1 rounded text-xs font-semibold bg-[#fef08a] hover:bg-[#facc15] text-gray-900"
                              >
                                LAB HOLD
                              </button>
                              <button
                                onClick={() => handleHoldAction(wo, "QC_HOLD")}
                                className="px-2 py-1 rounded text-xs font-semibold bg-[#f97316] hover:bg-[#ea580c] text-gray-900"
                              >
                                QC HOLD
                              </button>
                            </div>
                          )}
                        {wo.isTransferred && (
                          <div className="mt-2">
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-100 text-green-800 text-xs font-semibold rounded-full">
                              <svg
                                className="w-4 h-4"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                  clipRule="evenodd"
                                />
                              </svg>
                              Transferred
                            </span>
                          </div>
                        )}
                        {wo.room &&
                          wo.status !== "completed" &&
                          !isOnHold &&
                          !isReadOnly &&
                          canUnassign && (
                            <button
                              onClick={() =>
                                onContextMenuAction?.(wo, "unassign")
                              }
                              className="px-2 py-1 rounded text-xs font-semibold bg-black hover:bg-gray-900 text-white mt-1"
                            >
                              Unassign
                            </button>
                          )}
                        {isOnHold && (
                          <div className="mt-2 flex justify-center">
                            <span
                              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full
        bg-${wo.warehouse === "LAB HOLD" ? "yellow" : "orange"}-100
        text-${wo.warehouse === "LAB HOLD" ? "yellow" : "orange"}-800
        border border-${wo.warehouse === "LAB HOLD" ? "yellow" : "orange"}-300
      `}
                            >
                              UNDER {wo.warehouse}
                            </span>
                          </div>
                        )}
                        {isTask && !isCompleted && !isOnHold && !isReadOnly && (
                          <button
                            onClick={() =>
                              onContextMenuAction?.(wo, "removeTask", null)
                            }
                            className="px-2 py-1 rounded text-xs font-semibold bg-red-200 hover:bg-red-300 text-gray-900"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </>
                  )}
                  {isCustomTask && (
                    <>
                      <div className="font-medium text-purple-700">
                        Packing / Notes
                      </div>
                      <div>
                        <EditableCell
                          value={packingValues[wo._id] || ""}
                          onSave={(v) => updatePackingValue(wo._id, v)}
                          placeholder="Add packing / notes"
                          disabled={!canEditDistributors || isReadOnly}
                        />
                      </div>
                    </>
                  )}
                </div>
                {isEvent &&
                  wo.type === "manual cleaning" &&
                  onRemoveCleaningTask &&
                  !isReadOnly && (
                    <div className="mt-3">
                      {wo.isCustomTask && onRenameCleaningTask && (
                        <button
                          onClick={() =>
                            openRenameCustomTaskModal(
                              wo._id,
                              wo.title || "Custom Task",
                            )
                          }
                          className="px-2.5 py-1.5 text-xs rounded bg-blue-700 text-white hover:bg-blue-600 mr-2"
                        >
                          Rename
                        </button>
                      )}
                      <button
                        onClick={() => onRemoveCleaningTask(wo._id)}
                        className="px-2.5 py-1.5 text-xs rounded bg-red-100 text-red-700 hover:bg-red-200"
                      >
                        Remove
                      </button>
                    </div>
                  )}
              </div>
            );
          })
        ) : (
          <div className="text-center py-8 text-gray-600 text-sm">
            {isReadOnly
              ? "No work orders or tasks for this day"
              : "Drag a work order or task here!"}
          </div>
        )}

        <div className="px-3 py-2 border-b-4 border-blue-500 bg-blue-50/30 rounded-b-lg">
          <NoteRow
            note={bottomNote}
            noteType="bottom"
            onSave={saveRoomNote}
            placeholder="Pick up at 4pm, call supplier..."
            colorClass="text-blue-900 font-medium"
            counterClass="text-blue-600"
          />
        </div>
      </div>

      {/* Context Menu & Modal */}
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          wo={contextMenu.wo}
          onAction={handleContextMenuAction}
          onClose={() => setContextMenu(null)}
          noPermission={contextMenu.noPermission}
        />
      )}

      {isNoSelectionModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-4 sm:p-6 rounded-lg shadow-lg max-w-[90%] sm:max-w-sm w-full">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
              {errorMessage ? "Transfer Error" : "No Work Orders Selected"}
            </h3>
            <p className="text-xs sm:text-sm text-gray-600 mb-4 sm:mb-6">
              {errorMessage ||
                "Please select at least one completed work order to transfer."}
            </p>
            <button
              onClick={closeNoSelectionModal}
              className="w-full py-1 sm:py-2 px-3 sm:px-4 rounded bg-black hover:bg-gray-900 text-white text-xs sm:text-sm font-semibold"
            >
              OK
            </button>
          </div>
        </div>
      )}

      <MarkCompletedConfirmModal
        isOpen={showMarkConfirmModal}
        onClose={() => {
          setShowMarkConfirmModal(false);
          setConfirmChecked(false);
          setWoToMark(null);
        }}
        workOrder={woToMark}
        onSuccess={() => onRefreshRoom?.()}
      />

      {/* Split Work Order Modal */}
      {isSplitModalOpen && selectedWoForSplit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              Split Work Order: {selectedWoForSplit.woNumber}
            </h3>
            
            <div className="mb-4 text-sm text-gray-700 space-y-2">
              <div className="flex justify-between">
                <span>Original Planned Qty:</span>
                <span className="font-semibold">{selectedWoForSplit.plannedQty || 0}</span>
              </div>
              <div className="flex justify-between text-green-700">
                <span>Actual Completed Qty:</span>
                <span className="font-semibold">{selectedWoForSplit.actualQty || 0}</span>
              </div>
              <div className="flex justify-between pt-2 border-t font-semibold">
                <span>Remaining Qty:</span>
                <span>{(selectedWoForSplit.plannedQty || 0) - (selectedWoForSplit.actualQty || 0)}</span>
              </div>
            </div>

            <div className="mb-4">
              <label htmlFor="splitQtyInput" className="block text-sm font-medium text-gray-700 mb-1">
                Quantity to Move to Next Working Day:
              </label>
              <input
                id="splitQtyInput"
                type="number"
                min="1"
                max={(selectedWoForSplit.plannedQty || 0) - (selectedWoForSplit.actualQty || 0) - 1}
                value={splitQty}
                onChange={(e) => setSplitQty(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter split quantity..."
              />
            </div>

            <div className="mb-6">
              <label htmlFor="splitDateInput" className="block text-sm font-medium text-gray-700 mb-1">
                Select Date (Optional):
              </label>
              <input
                id="splitDateInput"
                type="date"
                value={splitDate}
                onChange={(e) => setSplitDate(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">If left blank, it will automatically default to the next working day.</p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setIsSplitModalOpen(false);
                  setSelectedWoForSplit(null);
                  setSplitDate("");
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  try {
                    await splitWorkOrder(selectedWoForSplit._id, Number(splitQty), splitDate || undefined);
                    toast.success("Work order split successfully!");
                    setIsSplitModalOpen(false);
                    setSelectedWoForSplit(null);
                    setSplitDate("");
                    onRefreshRoom?.();
                  } catch (error) {
                    toast.error(error.response?.data?.message || "Failed to split work order");
                  }
                }}
                disabled={!splitQty || Number(splitQty) <= 0 /* || Number(splitQty) >= ((selectedWoForSplit.plannedQty || 0) - (selectedWoForSplit.actualQty || 0)) */}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Create Split
              </button>
            </div>
          </div>
        </div>
      )}

      <RenameCustomTaskModal
        isOpen={renameModalState.isOpen}
        initialTitle={renameModalState.title}
        onClose={closeRenameCustomTaskModal}
        onConfirm={handleConfirmRenameCustomTask}
      />
    </div>
  );
};

export default WorkOrderTable;
